/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl, defineMessage} from 'react-intl'
import {
    Button,
    Text,
    useMultiStyleConfig,
    Box,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink, useHistory} from 'react-router-dom'

/**
 * The Swatch Component displays item inside `SwatchGroup`. For proper keyboard accessibility,
 * ensure that the rendered elements can receive keyboard focus, and are immediate siblings.
 */
const Swatch = ({
    children,
    disabled,
    href,
    label,
    name,
    selected,
    value,
    handleSelect,
    variant = 'square'
}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('SwatchGroup', {variant, disabled, selected, value})
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [selectHandlers, setSelectHandlers] = useState({})

    const onSelect = useCallback(
        (e) => {
            e.preventDefault()
            handleSelect(value)
        },
        [handleSelect]
    )

    useEffect(() => {
        if (!handleSelect) {
            return
        }

        setSelectHandlers({
            //[isDesktop ? 'onMouseEnter' : 'onClick']: onSelect
            ['onClick']: onSelect
        })
    }, [onSelect])

    // Define messages using defineMessage for unavailable colors and sizes
    const swatchMessages = {
        colorUnavailable: defineMessage({
            id: 'swatch.message.color',
            defaultMessage: 'The {name} color is unavailable'
        }),
        sizeUnavailable: defineMessage({
            id: 'swatch.message.size',
            defaultMessage: 'The {name} size is unavailable'
        })
    }

    const ariaLabel = disabled
        ? intl.formatMessage(
              variant === 'color'
                  ? swatchMessages.colorUnavailable
                  : swatchMessages.sizeUnavailable,
              {name}
          )
        : name

    const history = useHistory()

    const handleSizeSwatchesClick = (e) => {
        const encodedLink = e.currentTarget.getAttribute('data-url')

        if (encodedLink) {
            try {
                const decodedLink = atob(encodedLink)
                history.push(decodedLink)
            } catch (error) {
                // do nothing
            }
        }
    }

    return variant === 'size' && href ? (
        <Box
            {...styles.swatch}
            as="button"
            type="button"
            data-url={btoa(href)}
            aria-label={ariaLabel}
            aria-checked={selected}
            variant="outline"
            role="radio"
            tabIndex={selected ? '0' : '-1'}
            onClick={handleSizeSwatchesClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleSizeSwatchesClick(e)
                }
            }}
        >
            {children}
            {label && (
                <Text variant="bodySmall" {...styles.text}>
                    {label}
                </Text>
            )}
        </Box>
    ) : (
        <Button
            {...styles.swatch}
            as={variant === 'color' && href ? RouteLink : 'button'}
            to={href}
            role="radio"
            tabIndex={selected ? '0' : '-1'}
            aria-label={ariaLabel}
            aria-checked={selected}
            variant="outline"
            {...(href ? {} : selectHandlers)}
        >
            {children}
            {label && (
                <Text variant="bodySmall" {...styles.text}>
                    {label}
                </Text>
            )}
        </Button>
    )
}

Swatch.displayName = 'Swatch'

Swatch.propTypes = {
    /**
     * The children to be rendered within swatch item.
     */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    /**
     * Indicates whether the option is disabled
     */
    disabled: PropTypes.bool,
    /**
     * Indicates whether the option is selected.
     * This props is provided internally by SwatchGroup
     */
    selected: PropTypes.bool,
    /**
     * The shape of the Swatch
     */
    variant: PropTypes.oneOf(['color', 'size', 'buttonWithChip']),
    /**
     * The label of the option.
     */
    label: PropTypes.string,
    /**
     *  The url of this option
     */
    href: PropTypes.string,
    /**
     * The display value for each swatch
     */
    name: PropTypes.string,
    /**
     * The value for the option.
     */
    value: PropTypes.string,
    /**
     * Whether the swatch can receive tab focus
     */
    isFocusable: PropTypes.bool,
    /**
     * This function is called whenever the mouse enters the swatch on desktop or when clicked on mobile.
     * The values is passed as the first argument.
     */
    handleSelect: PropTypes.func
}

export default Swatch
